import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, fmtMoney } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, DataTable, Td, FormDialog, Field, TextInput, SelectInput, ActionButton, EmptyState } from "../../components/common";
import { UOMS } from "../../components/fleet/fields";

const EMPTY = { item_name: "", uom: "EA", sku: "", category: "", description: "", quantity_on_hand: "", reorder_point: "", storage_location: "", unit_cost: "", sell_price: "", supplier: "" };

export default function FleetInventory() {
  const { user } = useAuth();
  const canDelete = ["admin", "dispatcher"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/fleet/inventory").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      item_name: it.item_name || "", uom: it.uom || "EA", sku: it.sku || "", category: it.category || "", description: it.description || "",
      quantity_on_hand: it.quantity_on_hand ?? "", reorder_point: it.reorder_point ?? "",
      storage_location: it.storage_location || "", unit_cost: it.unit_cost ?? "", sell_price: it.sell_price ?? "", supplier: it.supplier || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { item_name: form.item_name, uom: form.uom };
    if (form.sku) payload.sku = form.sku;
    if (form.category) payload.category = form.category;
    if (form.description) payload.description = form.description;
    if (form.quantity_on_hand !== "") payload.quantity_on_hand = Number(form.quantity_on_hand);
    if (form.reorder_point !== "") payload.reorder_point = Number(form.reorder_point);
    if (form.storage_location) payload.storage_location = form.storage_location;
    if (form.unit_cost !== "") payload.unit_cost = Number(form.unit_cost);
    if (form.sell_price !== "") payload.sell_price = Number(form.sell_price);
    if (form.supplier) payload.supplier = form.supplier;
    try {
      if (editing) {
        await api.patch(`/fleet/inventory/${editing.id}`, payload);
        toast.success("Inventory item updated");
      } else {
        await api.post("/fleet/inventory", payload);
        toast.success("Inventory item added");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    try {
      await api.delete(`/fleet/inventory/${it.id}`);
      toast.success(`${it.item_name} deleted`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const isLow = (it) => it.reorder_point != null && (it.available_quantity ?? it.quantity_on_hand ?? 0) <= it.reorder_point;

  return (
    <div data-testid="fleet-inventory-page">
      <PageHeader title="Fleet Inventory" subtitle="Physical stock, work-order reservations, and available parts" testId="fleet-inventory-header">
        <Link to="/fleet/purchasing" data-testid="inventory-purchasing-link" className="text-sm text-[#84252F] underline underline-offset-4">Purchase Orders & Receiving</Link>
        <ActionButton testId="fleet-inventory-add-button" onClick={openAdd}>
          <Plus size={16} /> Add Inventory Item
        </ActionButton>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState message="No inventory items" testId="fleet-inventory-empty" />
      ) : (
        <DataTable
          testId="fleet-inventory-table"
          columns={["Item", "SKU / Part #", "Category", "Description", "Qty on Hand", "Reserved", "Available", "Reorder Pt", "Storage", "Unit Cost", "Sales Price", "Supplier", "Last Updated", ""]}
          rows={items}
          renderRow={(it) => (
            <tr key={it.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`fleet-inventory-row-${it.id}`}>
              <Td className="font-medium text-slate-100">{it.item_name}</Td>
              <Td className="font-mono text-xs text-slate-400">{it.sku || "—"}</Td>
              <Td className="text-slate-400">{it.category || "—"}</Td>
              <Td className="text-slate-400 max-w-xs truncate">{it.description || "—"}</Td>
              <Td>
                <span className={`font-mono ${isLow(it) ? "text-red-400" : "text-slate-100"}`}>{it.quantity_on_hand ?? 0}</span>
                {isLow(it) && <span className="ml-2 px-1.5 py-0.5 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-mono uppercase" data-testid={`fleet-inventory-low-${it.id}`}>Low</span>}
              </Td>
              <Td><span data-testid={`fleet-reserved-${it.id}`} className="font-mono text-amber-700">{it.reserved_quantity ?? 0}</span></Td>
              <Td><span data-testid={`fleet-available-${it.id}`} className="font-mono font-semibold">{it.available_quantity ?? it.quantity_on_hand ?? 0}</span></Td>
              <Td className="font-mono text-slate-400">{it.reorder_point ?? "—"}</Td>
              <Td className="text-slate-400">{it.storage_location || "—"}</Td>
              <Td className="font-mono">{it.unit_cost != null ? fmtMoney(it.unit_cost) : "—"}</Td>
              <Td className="font-mono text-emerald-600">{it.sell_price != null ? fmtMoney(it.sell_price) : "—"}</Td>
              <Td className="text-slate-400">{it.supplier || "—"}</Td>
              <Td className="text-slate-500 text-xs">{it.last_updated ? fmtDate(it.last_updated) : "—"}</Td>
              <Td>
                <div className="flex gap-1">
                  <button data-testid={`fleet-inventory-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 text-slate-500 hover:text-amber-400 transition-colors"><Pencil size={14} /></button>
                  {canDelete && <button data-testid={`fleet-inventory-delete-${it.id}`} onClick={() => remove(it)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>}
                </div>
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }} title={editing ? `Edit ${editing.item_name}` : "Add Inventory Item"} testId="fleet-inventory-dialog">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Item name">
              <TextInput testId="inv-name-input" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} required placeholder='e.g. "Air Filter"' />
            </Field>
            <Field label="SKU / Part number">
              <TextInput testId="inv-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="OEM # / barcode" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <TextInput testId="inv-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Filters, Fluids, Hardware…" />
            </Field>
            <Field label="Supplier / Vendor">
              <TextInput testId="inv-supplier-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </Field>
          </div>
          <Field label="Description">
            <TextInput testId="inv-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Specs, size, compatibility notes" />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Qty on hand">
              <TextInput testId="inv-qty-input" type="number" step="1" min="0" value={form.quantity_on_hand} onChange={(e) => setForm({ ...form, quantity_on_hand: e.target.value })} />
            </Field>
            <Field label="Reorder point">
              <TextInput testId="inv-reorder-input" type="number" step="1" min="0" value={form.reorder_point} onChange={(e) => setForm({ ...form, reorder_point: e.target.value })} />
            </Field>
            <Field label="Unit cost ($)">
              <TextInput testId="inv-cost-input" type="number" step="0.01" min="0" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
            </Field>
            <Field label="Sales price ($)">
              <TextInput testId="inv-sell-price-input" type="number" step="0.01" min="0" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} />
            </Field>
          </div>
          <Field label="Unit of Measure"><SelectInput testId="inv-uom-input" options={UOMS} value={form.uom} onChange={(e) => setForm({ ...form, uom: e.target.value })} /></Field>
          <Field label="Storage location">
            <TextInput testId="inv-storage-input" value={form.storage_location} onChange={(e) => setForm({ ...form, storage_location: e.target.value })} placeholder='e.g. "Shelf 2A"' />
          </Field>
          <ActionButton testId="inv-submit-button" type="submit" disabled={busy} className="w-full justify-center">
            {busy ? "Saving…" : editing ? "Save Changes" : "Add Item"}
          </ActionButton>
        </form>
      </FormDialog>
    </div>
  );
}
